import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Bar, Pie, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import './CommonChart.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const DEFAULT_COLORS = [
  '#4e79a7', '#f28e2b', '#e15759', '#76b7b2', '#59a14f',
  '#edc948', '#b07aa1', '#ff9da7', '#9c755f', '#bab0ac',
  '#1f77b4', '#ff7f0e', '#2ca02c', '#d62728', '#9467bd',
  '#8c564b', '#e377c2', '#7f7f7f', '#bcbd22', '#17becf',
  '#393b79', '#637939', '#8c6d31', '#843c39', '#7b4173',
  '#5254a3', '#8ca252', '#bd9e39', '#ad494a', '#a55194',
];

// Generates a distinct color via the golden-angle hue rotation so charts with more
// categories than the static palette never reuse colors.
const getChartColor = (index) => {
  if (index < DEFAULT_COLORS.length) return DEFAULT_COLORS[index];
  const offset = index - DEFAULT_COLORS.length;
  const hue = (offset * 137.508) % 360;
  const saturation = 62 + ((offset * 13) % 18); // 62%–80%
  const lightness = 48 + ((offset * 7) % 14);   // 48%–62%
  return `hsl(${hue.toFixed(0)}, ${saturation}%, ${lightness}%)`;
};

const DEFAULT_CHART_TYPES = [
  { type: 'square', label: 'Square Widget', icon: 'square' },
  { type: 'bar',    label: 'Bar Chart',     icon: 'bar' },
  { type: 'line',   label: 'Line Chart',    icon: 'line' },
  { type: 'pie',    label: 'Pie Chart',     icon: 'pie' },
  { type: 'column', label: 'Column Chart',  icon: 'column' },
];

const dashboardChartTypes = {};
const dashboardVisibleWidgets = {};
const dashboardCustomTitles = {};
const DASHBOARD_CHART_TYPES_APPLIED_EVENT = 'dashboard-chart-types-applied';
const DASHBOARD_VISIBLE_WIDGETS_APPLIED_EVENT = 'dashboard-visible-widgets-applied';
const DASHBOARD_CUSTOM_TITLES_APPLIED_EVENT = 'dashboard-custom-titles-applied';

export const getDashboardChartTypes = () => ({ ...dashboardChartTypes });
export const getDashboardVisibleWidgets = () => ({ ...dashboardVisibleWidgets });
export const getDashboardCustomTitles = () => ({ ...dashboardCustomTitles });

export const setDashboardCustomTitle = (widgetKey, customTitle) => {
  if (!widgetKey) return;
  const trimmed = (customTitle || '').trim();
  if (!trimmed) {
    delete dashboardCustomTitles[widgetKey];
  } else {
    dashboardCustomTitles[widgetKey] = trimmed;
  }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(DASHBOARD_CUSTOM_TITLES_APPLIED_EVENT));
  }
};

export const getEffectiveWidgetTitle = (widgetKey, fallback) => {
  if (widgetKey && dashboardCustomTitles[widgetKey]) {
    return dashboardCustomTitles[widgetKey];
  }
  return fallback;
};

export const applyDashboardCustomTitles = (customTitles = {}) => {
  Object.keys(dashboardCustomTitles).forEach((key) => {
    delete dashboardCustomTitles[key];
  });
  Object.keys(customTitles || {}).forEach((key) => {
    if (customTitles[key]) {
      dashboardCustomTitles[key] = customTitles[key];
    }
  });
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(DASHBOARD_CUSTOM_TITLES_APPLIED_EVENT));
  }
};

export const setDashboardWidgetVisibility = (widgetKey, isVisible) => {
  if (!widgetKey) return;
  dashboardVisibleWidgets[widgetKey] = isVisible;
};

export const isDashboardWidgetVisible = (widgetKey) => {
  if (!widgetKey) return true;
  return dashboardVisibleWidgets[widgetKey] !== false;
};

export const applyDashboardVisibleWidgets = (visibleWidgets = {}) => {
  Object.keys(dashboardVisibleWidgets).forEach((key) => {
    delete dashboardVisibleWidgets[key];
  });
  Object.keys(visibleWidgets || {}).forEach((key) => {
    dashboardVisibleWidgets[key] = visibleWidgets[key];
  });

  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(DASHBOARD_VISIBLE_WIDGETS_APPLIED_EVENT));
  }
};

export const applyDashboardChartTypes = (chartTypes = {}) => {
  Object.keys(dashboardChartTypes).forEach((key) => {
    delete dashboardChartTypes[key];
  });
  Object.keys(chartTypes || {}).forEach((key) => {
    dashboardChartTypes[key] = chartTypes[key];
  });

  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(DASHBOARD_CHART_TYPES_APPLIED_EVENT));
  }
};

const Icon = ({ name }) => {
  const common = { width: 16, height: 16, viewBox: '0 0 24 24', fill: 'currentColor' };
  const barSvg = (
    <svg {...common}>
      <rect x="3" y="11" width="4" height="9" />
      <rect x="10" y="6" width="4" height="14" />
      <rect x="17" y="14" width="4" height="6" />
    </svg>
  );
  switch (name) {
    case 'square':
      return (
        <svg {...common}>
          <rect x="3" y="4" width="18" height="16" rx="2" fill="none" stroke="currentColor" strokeWidth="2" />
          <line x1="3" y1="9" x2="21" y2="9" stroke="currentColor" strokeWidth="2" />
        </svg>
      );
    case 'bar':
      return barSvg;
    case 'column':
      return (
        <svg {...common}>
          <rect x="3" y="13" width="4" height="7" />
          <rect x="10" y="9" width="4" height="11" />
          <rect x="17" y="5" width="4" height="15" />
        </svg>
      );
    case 'line':
      return (
        <svg {...common}>
          <polyline
            points="3,16 8,12 13,14 18,8 21,10"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <circle cx="8" cy="12" r="1.5" />
          <circle cx="13" cy="14" r="1.5" />
          <circle cx="18" cy="8" r="1.5" />
        </svg>
      );
    case 'pie':
      return (
        <svg {...common}>
          <path d="M12 2v10l8.66 5A10 10 0 1 1 12 2z" />
        </svg>
      );
    case 'menu':
    default:
      return barSvg;
  }
};

Icon.propTypes = { name: PropTypes.string };

const CommonChart = ({
  title,
  subtitle,
  type: typeProp,
  defaultType = 'bar',
  onTypeChange,
  chartTypes = DEFAULT_CHART_TYPES,
  showHeader = true,
  data = {},
  loading = false,
  height = 300,
  width = '100%',
  options: optionsOverride,
  onClick,
  onOptionChange,
  className = '',
  style,
  csvExport = false,
  widgetKey,
  onTitleChange,
  editableTitle = false,
  valuePrefix = '',
}) => {
  const [visibilityVersion, setVisibilityVersion] = useState(0);
  const isControlled = typeProp !== undefined;
  const [internalType, setInternalType] = useState(() => {
    if (title && dashboardChartTypes[title]) {
      return dashboardChartTypes[title];
    }
    return defaultType;
  });
  const type = isControlled ? typeProp : internalType;

  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);

  useEffect(() => {
    if (isControlled || !title) return;
    const savedType = dashboardChartTypes[title];
    if (savedType && savedType !== internalType) {
      setInternalType(savedType);
    }
  }, [isControlled, title, internalType]);

  useEffect(() => {
    if (isControlled || !title || typeof window === 'undefined') return undefined;

    const syncChartTypeFromPreference = () => {
      const savedType = dashboardChartTypes[title];
      if (savedType) {
        setInternalType(savedType);
      }
    };

    window.addEventListener(DASHBOARD_CHART_TYPES_APPLIED_EVENT, syncChartTypeFromPreference);
    return () => {
      window.removeEventListener(DASHBOARD_CHART_TYPES_APPLIED_EVENT, syncChartTypeFromPreference);
    };
  }, [isControlled, title]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const syncVisibilityFromPreference = () => {
      setVisibilityVersion((version) => version + 1);
    };

    window.addEventListener(DASHBOARD_VISIBLE_WIDGETS_APPLIED_EVENT, syncVisibilityFromPreference);
    return () => {
      window.removeEventListener(DASHBOARD_VISIBLE_WIDGETS_APPLIED_EVENT, syncVisibilityFromPreference);
    };
  }, []);

  // Title editing state ----------------------------------------------------
  const titleLookupKey = widgetKey || title;
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [titleVersion, setTitleVersion] = useState(0);
  const [draftTitle, setDraftTitle] = useState('');
  const titleInputRef = useRef(null);

  const effectiveTitle = useMemo(() => {
    if (titleLookupKey && dashboardCustomTitles[titleLookupKey]) {
      return dashboardCustomTitles[titleLookupKey];
    }
    return title;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [titleLookupKey, title, titleVersion]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const syncTitlesFromPreference = () => setTitleVersion((v) => v + 1);
    window.addEventListener(DASHBOARD_CUSTOM_TITLES_APPLIED_EVENT, syncTitlesFromPreference);
    return () => {
      window.removeEventListener(DASHBOARD_CUSTOM_TITLES_APPLIED_EVENT, syncTitlesFromPreference);
    };
  }, []);

  useEffect(() => {
    if (isEditingTitle && titleInputRef.current) {
      titleInputRef.current.focus();
      titleInputRef.current.select();
    }
  }, [isEditingTitle]);

  const startEditingTitle = () => {
    if (!editableTitle) return;
    setDraftTitle(effectiveTitle || '');
    setIsEditingTitle(true);
  };

  const commitTitle = () => {
    if (!editableTitle) return;
    const next = (draftTitle || '').trim();
    setIsEditingTitle(false);
    if (!titleLookupKey) return;
    // If unchanged (matches original cfg title and there was no override) skip
    const previousOverride = dashboardCustomTitles[titleLookupKey] || '';
    const normalizedNext = next === title ? '' : next;
    if (normalizedNext === previousOverride) return;
    if (normalizedNext) {
      dashboardCustomTitles[titleLookupKey] = normalizedNext;
    } else {
      delete dashboardCustomTitles[titleLookupKey];
    }
    setTitleVersion((v) => v + 1);
    if (onTitleChange) onTitleChange(titleLookupKey, normalizedNext);
  };

  const cancelEditingTitle = () => {
    setIsEditingTitle(false);
  };

  const handleTypeChange = (newType) => {
    setMenuOpen(false);
    if (title) {
      dashboardChartTypes[title] = newType;
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent(DASHBOARD_CHART_TYPES_APPLIED_EVENT));
      }
    }
    if (!isControlled) setInternalType(newType);
    if (onTypeChange) onTypeChange(newType);
  };

  const handleCsvExport = () => {
    const safeData = data || {};
    const isTable = type === 'table';
    let csvContent = '';

    if (isTable) {
      const columns = safeData.columns || [];
      const rows = safeData.rows || [];
      csvContent = columns.map(c => `"${c.header}"`).join(',') + '\n';
      csvContent += rows.map(row =>
        columns.map(c => `"${row[c.key] !== undefined && row[c.key] !== null ? row[c.key] : ''}"`).join(',')
      ).join('\n');
    } else {
      const labels = safeData.labels || [];
      const datasets = safeData.datasets || [];
      const headers = ['Label', ...datasets.map(ds => ds.label || 'Value')];
      csvContent = headers.map(h => `"${h}"`).join(',') + '\n';
      csvContent += labels.map((label, i) =>
        [`"${label}"`, ...datasets.map(ds => ds.data?.[i] ?? '')].join(',')
      ).join('\n');
    }

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `${(title || 'widget').replaceAll(/[^a-zA-Z0-9]/g, '-').toLowerCase()}.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const chartData = useMemo(() => {
    const safeData = data || {};
    const labels = safeData.labels || [];
    const datasetCount = (safeData.datasets || []).length;
    const usePerSliceColors =
      type === 'pie' ||
      type === 'doughnut' ||
      ((type === 'bar' || type === 'column') && datasetCount === 1);
    const datasets = (safeData.datasets || []).map((ds, i) => {
      // For pie/doughnut and single-dataset bar/column, always generate a unique color
      // per slice/bar from the local palette so categories never repeat colors even if
      // the API returned a short backgroundColor array.
      let backgroundColor;
      if (usePerSliceColors) {
        backgroundColor = labels.map((_, idx) => getChartColor(idx));
      } else if (ds.backgroundColor) {
        backgroundColor = ds.backgroundColor;
      } else {
        backgroundColor = getChartColor(i);
      }
      const isLine = type === 'line';
      const lineColor = ds.borderColor || (Array.isArray(backgroundColor) ? backgroundColor[0] : backgroundColor) || getChartColor(i);
      return {
        label: ds.label || '',
        data: ds.data || [],
        backgroundColor,
        borderColor: isLine ? lineColor : undefined,
        borderWidth: isLine ? 3 : 1,
        ...(isLine && {
          tension: 0.3,
          pointRadius: 3,
          pointHoverRadius: 5,
          pointBorderWidth: 2,
          fill: false,
        }),
      };
    });
    return { labels, datasets };
  }, [data, type, visibilityVersion]);

  const chartOptions = useMemo(() => {
    const formatValue = (raw) => {
      const num = Number(raw);
      if (!Number.isFinite(num)) return String(raw ?? '');
      const fixed = Number.isInteger(num) ? num.toLocaleString() : num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      return `${valuePrefix || ''}${fixed}`;
    };
    const base = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: type === 'pie' || chartData.datasets.length > 1 },
        title: { display: false },
        tooltip: {
          callbacks: {
            label: (ctx) => {
              let lbl = '';
              if (ctx.dataset?.label) {
                lbl = `${ctx.dataset.label}: `;
              } else if (ctx.label) {
                lbl = `${ctx.label}: `;
              }
              let raw = ctx.parsed;
              if (raw && typeof raw === 'object') {
                // For horizontal bar charts (indexAxis = 'y'), the value lives
                // on parsed.x (parsed.y is just the category index). For all
                // other chart types the value is on parsed.y.
                if (type === 'bar') {
                  raw = raw.x ?? raw.y ?? ctx.raw;
                } else {
                  raw = raw.y ?? raw.x ?? ctx.raw;
                }
              }
              if (raw === undefined || raw === null) raw = ctx.raw;
              return `${lbl}${formatValue(raw)}`;
            }
          }
        }
      },
    };
    if (type === 'bar' || type === 'column') {
      base.indexAxis = type === 'bar' ? 'y' : 'x';
      // The value axis is whichever one is NOT the indexAxis. Only the value
      // axis should be formatted with the currency / number formatter; the
      // category axis must show the raw label (e.g. date strings).
      const valueAxis = type === 'bar' ? 'x' : 'y';
      const categoryAxis = type === 'bar' ? 'y' : 'x';
      base.scales = {
        [valueAxis]: { beginAtZero: true, ticks: { callback: (v) => formatValue(v) } },
        [categoryAxis]: { beginAtZero: true },
      };
    }
    if (type === 'line') {
      base.elements = {
        line: {
          tension: 0.4,
        },
        point: {
          radius: 3,
        },
      };
      base.scales = {
        x: { beginAtZero: true },
        y: { beginAtZero: true, ticks: { callback: (v) => formatValue(v) } },
      };
    }
    return { ...base, ...(optionsOverride || {}) };
  }, [type, chartData.datasets.length, optionsOverride, valuePrefix]);

  const handleChartClick = (event, elements, chart) => {
    if (!onClick || !elements || elements.length === 0) return;
    const el = elements[0];
    onClick({
      datasetIndex: el.datasetIndex,
      index: el.index,
      label: chart.data.labels[el.index],
      value: chart.data.datasets[el.datasetIndex].data[el.index],
    });
  };

  const renderBody = () => {
    if (loading) {
      return (
        <div className="chart-loading">
          <div className="spinner"></div>
          <p>Loading chart data...</p>
        </div>
      );
    }

    const safeRenderData = data || {};
    const hasLabels = Array.isArray(safeRenderData.labels) && safeRenderData.labels.length > 0;
    const hasExplicitValue = safeRenderData.value !== undefined && safeRenderData.value !== null;
    const squareFallbackValue = Array.isArray(safeRenderData.datasets)
      ? safeRenderData.datasets.reduce((total, dataset) => {
          const datasetTotal = Array.isArray(dataset?.data)
            ? dataset.data.reduce((sum, item) => sum + (Number(item) || 0), 0)
            : 0;
          return total + datasetTotal;
        }, 0)
      : 0;
    const squareDisplayValue = hasExplicitValue ? safeRenderData.value : squareFallbackValue;
    const formatSquareValue = (val) => {
      const num = Number(val);
      if (!Number.isFinite(num)) return String(val ?? '');
      const fixed = Number.isInteger(num) ? num.toLocaleString() : num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      return `${valuePrefix || ''}${fixed}`;
    };
    const formattedSquareValue = valuePrefix ? formatSquareValue(squareDisplayValue) : squareDisplayValue;

    if (type === 'square') {
      const SquareTag = onClick ? 'button' : 'div';
      const squareInteractiveProps = onClick
        ? {
            type: 'button',
            onClick: () => onClick(safeRenderData),
            onKeyDown: (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick(safeRenderData);
              }
            },
          }
        : {};
      return (
        <div className="common-chart common-chart--square">
          <SquareTag
            className="common-chart__square-content"
            aria-label={`${title} ${formattedSquareValue}`}
            title={title}
            {...squareInteractiveProps}
          >
            <h2 className="widget-count">{formattedSquareValue}</h2>
          </SquareTag>
          {Array.isArray(safeRenderData.options) && safeRenderData.options.length > 0 && (
            <fieldset className="widget-options" aria-label="Widget options">
              {safeRenderData.options.map((opt) => (
                <button
                  key={opt.label}
                  type="button"
                  className={`option-button${
                    opt.label === safeRenderData.currentOption?.label ? ' active-option' : ''
                  }`}
                  onClick={() => onOptionChange?.(opt)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onOptionChange?.(opt);
                    }
                  }}
                  aria-label={`${opt.label}: ${opt.value}`}
                  aria-pressed={opt.label === safeRenderData.currentOption?.label}
                >
                  <div className="option-content">
                    <span className="option-value">({opt.value})</span>
                    <span className="option-label">{opt.label}</span>
                  </div>
                </button>
              ))}
            </fieldset>
          )}
        </div>
      );
    }

    if (type === 'table') {
      const columns = safeRenderData.columns || [];
      const rows = safeRenderData.rows || [];
      return (
        <div className="common-chart common-chart--table">
          <table className="widget-table" aria-label={title || 'Table'} role="table">
            <caption className="sr-only">{title || ''}</caption>
            <thead>
              <tr>
                {columns.map((col) => (
                  <th key={col.key} scope="col" title={col.header}>{col.header}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rIdx) => {
                const rowInteractiveProps = onClick
                  ? {
                      onClick: () => onClick(row),
                      onKeyDown: (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          onClick(row);
                        }
                      },
                      tabIndex: 0,
                      'aria-label': `Row ${rIdx + 1}`,
                    }
                  : {};
                return (
                  <tr
                    key={row.id ?? rIdx}
                    {...rowInteractiveProps}
                  >
                    {columns.map((col) => (
                      <td key={col.key} data-label={col.header}>
                        {col.render ? col.render(row) : row[col.key]}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      );
    }

    if (!hasLabels) {
      return (
        <div className="chart-empty">
          <p>No data available</p>
        </div>
      );
    }

    const finalOptions = { ...chartOptions, onClick: handleChartClick };
    const renderChartByType = () => {
      if (type === 'pie') {
        return <Pie data={chartData} options={finalOptions} />;
      }
      if (type === 'line') {
        return <Line data={chartData} options={finalOptions} />;
      }
      return <Bar data={chartData} options={finalOptions} />;
    };
    return (
      <div
        className={`common-chart common-chart--${type}`}
        style={{ width: '100%', height }}
      >
        {renderChartByType()}
      </div>
    );
  };

  if (!isDashboardWidgetVisible(widgetKey || title)) {
    return null;
  }

  const titleDisplayProps = editableTitle ? {
    role: 'button',
    tabIndex: 0,
    onDoubleClick: startEditingTitle,
    onKeyDown: (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        startEditingTitle();
      }
    }
  } : {};

  return (
    <div
      className={`common-chart-card ${className}`}
      style={{ width, ...style }}
    >
      {showHeader && (
        <div className="common-chart-card__header">
          <div className="common-chart-card__header-text">
            {isEditingTitle ? (
              <input
                ref={titleInputRef}
                type="text"
                className="common-chart-card__title-input"
                value={draftTitle}
                onChange={(e) => setDraftTitle(e.target.value)}
                onBlur={commitTitle}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    commitTitle();
                  } else if (e.key === 'Escape') {
                    e.preventDefault();
                    cancelEditingTitle();
                  }
                }}
                maxLength={80}
                aria-label="Edit widget name"
              />
            ) : (
              <div
                className={`common-chart-card__title${editableTitle ? ' is-editable' : ''}`}
                title={editableTitle ? 'Double-click to rename' : undefined}
                {...titleDisplayProps}
              >
                <span>{effectiveTitle}</span>
                {editableTitle && (
                  <button
                    type="button"
                    className="common-chart-card__rename-btn"
                    onClick={(e) => { e.stopPropagation(); startEditingTitle(); }}
                    aria-label="Rename widget"
                    title="Rename widget"
                  >
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                      <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1 1 0 0 0 0-1.41l-2.34-2.34a1 1 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
                    </svg>
                  </button>
                )}
              </div>
            )}
            {subtitle && <div className="common-chart-card__subtitle">{subtitle}</div>}
          </div>
          <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
          {csvExport && !loading && (
            <button
              type="button"
              className="common-chart-card__menu-btn"
              onClick={handleCsvExport}
              title="Export as CSV"
              aria-label="Export as CSV"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zm-1 1.5L18.5 9H13V3.5zM8 17l-3-3 1.4-1.4L8 14.2l5.6-5.6L15 10l-7 7z"/>
              </svg>
            </button>
          )}
          {chartTypes && chartTypes.length > 0 && (
            <div ref={menuRef} style={{ position: 'relative' }}>
              <button
                type="button"
                className="common-chart-card__menu-btn"
                onClick={() => setMenuOpen((o) => !o)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setMenuOpen((o) => !o);
                  }
                  if (e.key === 'Escape') {
                    setMenuOpen(false);
                  }
                }}
                aria-label="Switch chart type"
                aria-expanded={menuOpen}
                title="Click to change chart type"
              >
                <Icon name="menu" />
              </button>
              {menuOpen && (
                <div className="common-chart-card__menu" role="menu" aria-label="Chart type options">
                  {chartTypes.map((opt) => (
                    <button
                      key={opt.type}
                      type="button"
                      role="menuitem"
                      className={`common-chart-card__menu-item${
                        opt.type === type ? ' is-active' : ''
                      }`}
                      onClick={() => handleTypeChange(opt.type)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleTypeChange(opt.type);
                        }
                      }}
                      aria-label={`Change to ${opt.label}`}
                      title={opt.label}
                    >
                      <span className="common-chart-card__menu-icon">
                        <Icon name={opt.icon || opt.type} />
                      </span>
                      <span>{opt.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          </div>
        </div>
      )}
      <div className="common-chart-card__body">{renderBody()}</div>
    </div>
  );
};

CommonChart.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
  type: PropTypes.oneOf(['square', 'bar', 'line', 'column', 'pie', 'table']),
  defaultType: PropTypes.oneOf(['square', 'bar', 'line', 'column', 'pie', 'table']),
  onTypeChange: PropTypes.func,
  chartTypes: PropTypes.array,
  showHeader: PropTypes.bool,
  data: PropTypes.object,
  loading: PropTypes.bool,
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  options: PropTypes.object,
  onClick: PropTypes.func,
  onOptionChange: PropTypes.func,
  className: PropTypes.string,
  style: PropTypes.object,
  csvExport: PropTypes.bool,
  widgetKey: PropTypes.string,
  onTitleChange: PropTypes.func,
  editableTitle: PropTypes.bool,
  valuePrefix: PropTypes.string,
};

export default CommonChart;
